import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import context, { Device, DeviceValues } from '../context/shared';
import { centerClient } from '../../../shared/clients';
import type { AddDeviceModel, CommandResult, Account } from '../../../shared/generated';
import { IDevicesColDef } from '../DevicesTable';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { MenuProps, OutlinedInputProps } from '../../muiStyles/muiInput';

interface IAddDeviceFormProps {
    show: boolean;
    tableColumns: IDevicesColDef[];
    onClose: () => void;
    account: Account;
}

const fieldPairs: string[][] = [
    ['Balance', 'Cost'],
    ['Charge', 'StoragePeriod']
];


const AddDeviceForm = (props: IAddDeviceFormProps) => {
    const { show, tableColumns, account, onClose } = props;
    const [isAdding, setIsAdding] = useState(false);
    const [snIntervalStart, setSnIntervalStart] = useState<number>(0);
    const [count, setCount] = useState<number>(1);

    const { register, handleSubmit, formState: { errors }, reset } = useForm<DeviceValues>();
    const { refreshTree, deviceModels } = context;

    const errorRequiredStr = 'Необходимо заполнить поле ';
    const errorDefaultStr = 'Ошибка поля ';

    // Не мутируем исходный tableColumns, создаём вычисляемый массив колонок
    const columns = useMemo(() => {
        const cols = [...tableColumns];
        if (!cols.some(e => e.field === 'Model')) {
            cols.unshift({
                field: 'Model',
                headerName: 'Модель',
                context: { defaultValue: 25 }
            });
        }
        return cols;
    }, [tableColumns]);

    const getDefaultValues = (): DeviceValues => {
        return columns.reduce((acc: DeviceValues, column) => {
            acc[column.field ?? ''] = column.context?.defaultValue ?? '';
            return acc;
        }, {});
    };

    const [values, setValues] = useState<DeviceValues>(() => getDefaultValues());

    useEffect(() => {
        // При открытии/закрытии формы — сбрасываем значения формы и инпуты
        if (!show) {
            setSnIntervalStart(0);
            setCount(1);
            setValues(getDefaultValues());
            reset();
        } else {
            // при открытии обновляем значения из колонок (например, если deviceModels загрузились позже)
            setValues(getDefaultValues());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, columns]);

    const InputProps = {
        color: 'var(--text)',
        border: 'var(--border)',
        '&.Mui-focused': {
            color: 'var(--selected)',
            borderColor: 'var(--selected)'
        }
    };

    const handleClose = () => {
        onClose();
    };

    const successAdd = (res?: CommandResult) => {
        if (res?.Success) {
            handleClose();
            toast.success('Устройства добавлены!');
            refreshTree();
            setValues(getDefaultValues());
        } else {
            refreshTree();
            toast.error(res?.Description ?? 'Неизвестная ошибка');
        }
        setIsAdding(false);
    };

    const errorFunc = () => {
        setIsAdding(false);
    };

    const handleAddSubmit = async (formData: DeviceValues) => {
        const device: Device = new Device(formData);
        const snIntervalEnd = snIntervalStart + count - 1;
        if (snIntervalEnd < snIntervalStart) {
            toast.warn('Ошибка набора для с/н!');
            return;
        }

        device.Logins = [];
        if (account.Login)
            device.Logins.push(account.Login);

        const deviceModel: AddDeviceModel = {
            SerialFrom: snIntervalStart,
            SerialTo: snIntervalEnd,
            Device: device,
            ClientId: account.ClientId,
            TestMode: !!device.TestMode
        };

        setIsAdding(true);
        centerClient.addDevices(deviceModel)
            .then((res) => successAdd(res.data))
            .catch(() => errorFunc());
    };

    // Рендер-генераторы полей
    const renderCheckbox = (column: IDevicesColDef & { field: string }) => {
        const field = column.field;
        return (
            <>
                <Form.Check
                    type="checkbox"
                    id={`default-${field}`}
                    label={column.headerName}
                    {...register(field, { required: column.context?.required })}
                    checked={!!values[field]}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setValues({ ...values, [e.target.name]: e.target.checked })
                    }
                    name={field}
                />
                {errors?.[field] && <p>{errorRequiredStr + column.headerName}.</p>}
            </>
        );
    };

    const renderModelField = (column: IDevicesColDef & { field: string }) => {
        const field = column.field;
        const fieldErrors = errors?.[field];
        return (
            <>
                <FormControl sx={{ marginLeft: 0, width: 300 }}>
                    <InputLabel sx={InputProps}>{column.headerName}</InputLabel>
                    <Select
                        labelId="model-label"
                        id="model-select"
                        label={column.headerName}
                        value={values['Model'] ?? ''}
                        input={<OutlinedInput sx={OutlinedInputProps} label="Name" />}
                        {...register(field as keyof DeviceValues, { required: column.context?.required })}
                        MenuProps={MenuProps}
                        onChange={(e: SelectChangeEvent<string>) =>
                            setValues({ ...values, [e.target.name as string]: e.target.value })
                        }
                        name={field}
                    >
                        {deviceModels?.map((model) => (
                            <MenuItem key={model.ModelId} value={model.ModelId}>
                                {model.ModelName}
                            </MenuItem>
                        ))}
                    </Select>
                    {fieldErrors && fieldErrors.type === 'required' && <p>{errorRequiredStr + column.headerName}.</p>}
                    {fieldErrors && fieldErrors.type !== 'required' && <p>{errorDefaultStr + column.headerName}.</p>}
                </FormControl>

                <fieldset id="cnFieldset" style={{ marginTop: 12 }}>
                    <legend>с/н</legend>
                    <Form.Group style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Form.Label>c</Form.Label>
                        <Form.Control
                            style={{ width: '6rem' }}
                            name="snIntervalStart"
                            min={0}
                            value={snIntervalStart}
                            max={99999}
                            type="number"
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleSnStart(e)}
                            placeholder="с/н"
                        />
                        <Form.Label style={{ margin: '0 1rem' }}>по {(snIntervalStart + count - 1).toString().padStart(5, '0')}</Form.Label>
                        {/*<Form.Control*/}
                        {/*    style={{ marginLeft: '1rem' }}*/}
                        {/*    name="snIntervalEnd"*/}
                        {/*    min={0}*/}
                        {/*    max={99999}*/}
                        {/*    disabled*/}
                        {/*    value={snIntervalEnd}*/}
                        {/*    type="number"*/}
                        {/*    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleSnEnd(e)}*/}
                        {/*    placeholder="с/н"*/}
                        {/*/>*/}
                        <Form.Label>Кол-во: </Form.Label>
                        <Form.Control
                            style={{ width: '4rem' }}
                            name="devicesCount"
                            min={1}
                            max={100}
                            value={count}
                            type="number"
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleDevicesCount(e)}
                            placeholder="с/н"
                        />
                    </Form.Group>

                </fieldset>
            </>
        );
    };

    const renderDefaultField = (column: IDevicesColDef & { field: string }, type: string = 'text', maxLength?: number) => {
        const field = column.field;
        const fieldErrors = errors?.[field];

        let labelName = column.headerName;
        if (field === 'Charge') labelName = 'Аб. плата в месяц';

        let maxLengthError = 'Неверная длина поля ';
        if (field === 'Imei') maxLengthError = 'Поле не должно превышать 15 символов ';

        return (
            <>
                <Form.Label>{labelName}</Form.Label>
                <Form.Control
                    {...register(field as keyof DeviceValues, { required: column.context?.required, maxLength })}
                    type={type}
                    value={values[field] ?? ''}
                    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setValues({ ...values, [e.target.name]: e.target.value })
                    }
                    name={field}
                    placeholder={column.headerName}
                />
                {fieldErrors && fieldErrors.type === 'required' && <p>{errorRequiredStr + column.headerName}.</p>}
                {fieldErrors && fieldErrors.type === 'maxLength' && <p>{maxLengthError + column.headerName}.</p>}
                {fieldErrors && fieldErrors.type !== 'required' && fieldErrors.type !== 'maxLength' && <p>{errorDefaultStr + column.headerName}.</p>}
            </>
        );
    };

    const getDeviceCreatorFieldByType = (column: IDevicesColDef & { field: string }) => {
        switch (column.field) {
            case 'TestMode':
                return renderCheckbox(column);
            default:
                return getDeviceCreatorFieldByName(column);
        }
    };

    const getDeviceCreatorFieldByName = (column: IDevicesColDef & { field: string }) => {
        switch (column.field) {
            case 'Model':
                return renderModelField(column);
            case 'Imei': {
                if (count === 1) return renderDefaultField(column, 'number', 15);
                return <></>;
            }
            case 'Balance':
            case 'Cost':
            case 'Charge':
            case 'StoragePeriod':
                return renderDefaultField(column, 'number');
            default:
                return renderDefaultField(column);
        }
    };

    const handleSnStart = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const val = Number((e.target as HTMLInputElement).value) || 0;
        setSnIntervalStart(val);
    };

    const handleDevicesCount = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let val = Number((e.target as HTMLInputElement).value) || 1;
        if (val > 100)
            val = 100;
        setCount(val);
    };

    return (
        <Modal id="addDeviceContainer" show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Добавить контроллер</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={(e) => e.preventDefault()}>
                    <Form.Group>
                        {columns.map((column) => {
                            const meta = column.context;
                            // Пропускаем колонки без field или те, которые не используются для создания
                            if (meta?.needForCreate !== undefined && !meta.needForCreate) return null;
                            if (!column.field) return null;

                            const safeColumn = column as IDevicesColDef & { field: string };

                            const pair = fieldPairs.find(p => p[0] === safeColumn.field);
                            if (pair) {
                                const pairColumns = pair
                                    .map(fieldName => columns.find(c => c.field === fieldName))
                                    .filter((c): c is IDevicesColDef & { field: string } =>
                                        !!c && (c.context?.needForCreate === undefined || c.context.needForCreate));
                                // Второе поле пары уже отрисовано вместе с первым
                                if (pairColumns.length < 2) {
                                    if (pairColumns.length === 1 && pairColumns[0].field === safeColumn.field) {
                                        return (
                                            <div key={safeColumn.field}>
                                                {getDeviceCreatorFieldByType(pairColumns[0])}
                                            </div>
                                        );
                                    }
                                    return null;
                                }
                                return (
                                    <div key={safeColumn.field} style={{ display: 'flex', gap: '1rem' }}>
                                        {pairColumns.map(pc => (
                                            <div key={pc.field} style={{ flex: 1 }}>
                                                {getDeviceCreatorFieldByType(pc)}
                                            </div>
                                        ))}
                                    </div>
                                );
                            }

                            if (fieldPairs.some(p => p[1] === safeColumn.field)) return null;

                            return (
                                <div key={safeColumn.field}>
                                    {getDeviceCreatorFieldByType(safeColumn)}
                                </div>
                            );
                        })}
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {isAdding ? (
                    <>
                        <label>Добавление</label>
                        <ArrowClockwise className="spinner" />
                    </>
                ) : null}
                <Button onClick={handleSubmit((data) => handleAddSubmit(data))} variant="custom">
                    Добавить
                </Button>
                <Button variant="custom" onClick={handleClose}>
                    Закрыть
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddDeviceForm;